import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import apiClient from '../../services/api';

const JobAlertsApproval = () => {
  const [jobAlerts, setJobAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [approving, setApproving] = useState(null);
  const rowsPerPage = 10;

  useEffect(() => {
    loadJobAlerts();
  }, [page, search]);

  const loadJobAlerts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/jobalerts', {
        params: {
          limit: 200,
          isActive: 'any',
          search: search || undefined,
        },
      });

      if (response.data.success) {
        setJobAlerts(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to load job alerts');
      }
    } catch (err) {
      console.error('Error loading job alerts:', err);
      setError(err.response?.data?.message || 'Failed to load job alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (alertId) => {
    try {
      setApproving(alertId);
      await apiClient.patch(`/jobalerts/${alertId}/unflag`, {});
      await loadJobAlerts();
    } catch (err) {
      console.error('Error approving job alert:', err);
      setError(err.response?.data?.message || 'Failed to approve job alert');
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (alertId) => {
    try {
      setApproving(alertId);
      await apiClient.patch(`/jobalerts/${alertId}/move-to-inactive`, {});
      await loadJobAlerts();
    } catch (err) {
      console.error('Error rejecting job alert:', err);
      setError(err.response?.data?.message || 'Failed to reject job alert');
    } finally {
      setApproving(null);
    }
  };

  const filteredAlerts = jobAlerts.filter((alert) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      alert.notificationName?.toLowerCase().includes(searchLower) ||
      alert.organization?.toLowerCase().includes(searchLower) ||
      alert.category?.toLowerCase().includes(searchLower)
    );
  });

  const paginatedAlerts = filteredAlerts.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTimeWithSeconds = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch (error) {
      return '—';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Job Alerts Approval
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Search job alerts..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadJobAlerts}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Notification Name</strong></TableCell>
                  <TableCell><strong>Organization</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Last Date</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell><strong>Updated</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAlerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No job alerts found for approval
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAlerts.map((alert) => (
                    <TableRow key={alert.id} hover>
                      <TableCell>{alert.notificationName || '-'}</TableCell>
                      <TableCell>{alert.organization || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={alert.category || 'N/A'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatDate(alert.lastDate)}</TableCell>
                      <TableCell>{formatDateTimeWithSeconds(alert.createdAt)}</TableCell>
                      <TableCell>{formatDateTimeWithSeconds(alert.updatedAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={alert.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={alert.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {!alert.isActive && (
                            <IconButton
                              color="success"
                              onClick={() => handleApprove(alert.id)}
                              disabled={approving === alert.id}
                              size="small"
                              title="Activate"
                            >
                              {approving === alert.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <CheckCircleIcon />
                              )}
                            </IconButton>
                          )}
                          {alert.isActive && (
                            <IconButton
                              color="error"
                              onClick={() => handleReject(alert.id)}
                              disabled={approving === alert.id}
                              size="small"
                              title="Deactivate"
                            >
                              {approving === alert.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <CancelIcon />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredAlerts.length > rowsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={Math.ceil(filteredAlerts.length / rowsPerPage)}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default JobAlertsApproval;
